import React, { useState } from 'react';
import { useAuth } from 'context/AuthContext';
import { registrarTimeline } from 'services/iniciativa-action.service';
import type { EtapaCronograma } from 'utils/cronograma';
import './Timeline.scss';

interface TimelineProps {
  steps: EtapaCronograma[];
}

export const Timeline: React.FC<TimelineProps> = ({ steps }) => {
  const { user } = useAuth();
  const [hoveredStepId, setHoveredStepId] = useState<number | null>(null);

  const handleTimelineInteraction = async () => {
    if (!user?._id) {
      return;
    }

    try {
      await registrarTimeline(user._id);
    } catch (error) {
    }
  };

  const getProgressText = (step: EtapaCronograma): string => {
    const percentual = step.percentual || 0;
    return `${percentual}%`;
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'concluido': return 'Concluído';
      case 'em_andamento': return 'Em andamento';
      case 'pendente': return 'Pendente';
      default: return status;
    }
  };

  return (
    <div className="timeline" role="region" aria-label="Cronograma de etapas">
      {/* Cards dos passos - posicionados acima da timeline */}
      <div className="timeline__cards">
        {steps.map((step, index) => {
          // Calcular posição do card baseada na posição do ícone
          const totalSteps = steps.length;
          const padding = 0; // rem
          const edgeClass = index === 0 ? 'timeline__card--start' : index === totalSteps - 1 ? 'timeline__card--end' : '';
          
          // Posição horizontal calculada dinamicamente
          const leftPosition = index === 0 
            ? `${padding}rem` // alinhado ao início do container
            : index === totalSteps - 1
            ? `calc(100% - ${padding}rem - 24px)` // último ícone
            : `calc(${padding}rem + 24px + (100% - ${padding * 2}rem - 48px) * ${index} / ${totalSteps - 1})`;

          return (
            <div
              key={step.id}
              className={`timeline__card timeline__card--${step.status} ${edgeClass} ${
                step.status === 'em_andamento' || hoveredStepId === step.id ? 'timeline__card--visible' : ''
              }`}
              style={{ left: leftPosition }}
              role="group"
              aria-label={`Etapa ${step.id}: ${step.titulo} — ${getStatusLabel(step.status)}`}
            >
              {step.badge && (
                <div className="timeline__card-badge">{step.badge}</div>
              )}
              
              {/* Ícone Mobile */}
              <div className={`timeline__mobile-icon timeline__mobile-icon--${step.status}`} aria-hidden="true">
                {step.status === 'concluido' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                  </svg>
                )}
                {step.status === 'em_andamento' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="timeline__spinner" aria-hidden="true">
                    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" strokeDasharray="12 8" />
                    <circle cx="12" cy="12" r="1" fill="currentColor" />
                  </svg>
                )}
                {step.status === 'pendente' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" fill="currentColor"/>
                  </svg>
                )}
              </div>

              <span className="sr-only">Status: {getStatusLabel(step.status)}</span>
              <h3 className="timeline__card-title" tabIndex={0}>{step.titulo}</h3>
              <p className="timeline__card-description" tabIndex={0}>{step.descricao}</p>
              <p className="timeline__card-dates" tabIndex={0}>
                {step.dataFim ? (
                  <>De: <strong>{step.dataInicio}</strong> Até: <strong>{step.dataFim}</strong></>
                ) : (
                  <>A partir de: <strong>{step.dataInicio}</strong></>
                )}
              </p>

              {step.status === 'em_andamento' && step.percentual !== undefined && (
                <div className="timeline__card-progress">
                  <div 
                    className="timeline__progress-bar"
                    role="progressbar"
                    aria-valuenow={step.percentual}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={getProgressText(step)}
                  >
                    <div 
                      className="timeline__progress-fill"
                      style={{ width: `${step.percentual}%` }}
                    />
                  </div>
                  <p className="timeline__progress-text" tabIndex={0}>
                    {getProgressText(step)}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Linha do tempo com ícones */}
      <div className="timeline__track" role="list" aria-label="Progresso das etapas">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            {/* Linha conectora */}
            {index > 0 && (
              <div 
                className={`timeline__connector timeline__connector--${
                  steps[index - 1].status === 'concluido' ? 'completed' : 'locked'
                }`}
                aria-hidden="true"
              />
            )}
            
            {/* Ícone do passo */}
            <div 
              className={`timeline__icon timeline__icon--${step.status}`}
              onMouseEnter={() => {
                setHoveredStepId(step.id);
                handleTimelineInteraction();
              }}
              onMouseLeave={() => setHoveredStepId(null)}
              onClick={handleTimelineInteraction}
            >
              {step.status === 'concluido' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path 
                    d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" 
                    fill="currentColor"
                  />
                </svg>
              )}
              {step.status === 'em_andamento' && (
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none"
                  className="timeline__spinner"
                  aria-hidden="true"
                >
                  <circle 
                    cx="12" 
                    cy="12" 
                    r="8" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    strokeDasharray="12 8"
                  />
                </svg>
              )}
              {step.status === 'pendente' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path 
                    d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" 
                    fill="currentColor"
                  />
                </svg>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
